import React, { useEffect, useState } from 'react';
import { format, parse } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../../api/pagination';
import OnExitIcon from '../../../icons/OnExitIcon';
import OnFileDownloadIcon from '../../../icons/OnFileDownloadIcon';
import OnGroupIcon from '../../../icons/OnGroupIcon';
import { loadClaimQueueActionStatusDetails } from '../../../store/directusService';
import { clearClaimQueues, searchClaimQueue } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { getNameFromId } from '../../../utils/directusFunctions';
import { downloadCsv } from '../../../utils/downloadCSV';
import {
    CLAIM_EVENT_STATUSES_ERROR,
    CLAIM_EVENT_TYPES_ERROR,
    CLAIMS_ERROR,
    CONTRACT_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys, trimEntries } from '../../../utils/objectUtils';
import { stableSort } from '../../../utils/sortFunctions';
import { hasRole, MANAGER, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateRangePicker from '../../formElements/DateRangePicker';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import SearchOnEnter from '../../search/SearchOnEnter';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { validateFilters } from '../validateSearch';

import ActionEditor from './ActionEditor';
import AssigneeEditor from './AssigneeEditor';
import StatusEditor from './StatusEditor';
import ValidationTableRow from './ValidationTableRow';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import spinner from '../../../commonStyles/loadingSpinner.module.css';
import shared from '../claimStyles/claimSharedStyles.module.css';
import local from '../claimStyles/validations.module.css';

const headCells = [
    { id: 'checked', numeric: false, label: '', sortable: false },
    { id: 'ptCode', numeric: false, label: 'ID' },
    { id: 'poNumber', numeric: false, label: 'PO Number' },
    { id: 'eventType', numeric: false, label: 'Event Type', sortable: false },
    { id: 'eventDate', numeric: false, label: 'Event Date' },
    { id: 'lastStatusUpdated', numeric: false, label: 'Last Updated' },
    { id: 'statusName', numeric: false, label: 'Status', sortable: false },
    { id: 'actionStatus', numeric: false, label: 'Action Status', sortable: false },
    { id: 'edit', numeric: false, label: '', sortable: false }
];

const csvHeaderFields = 'PO Number,PT ID,Contract,Claim Status,Event Type,Event Date,Last Updated';
const unassigned = [
    {
        id: '00000000-0000-0000-0000-000000000001',
        name: 'Unassigned'
    }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'lastStatusUpdated',
    page: 0,
    rowsPerPage: DEFAULT_PAGE_LOAD_SIZE
};

const _NEW_SEARCH = 'Select filters or enter a PT ID or PO number to search';
const acceptedRoles = [PRAP, QUALITY, SUPERUSER];
const limitedAccessRoles = [MANAGER];

const ValidationTableManagement = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [catId, setCatId] = useState('');
    const [rows, setRows] = useState([]);
    const [qualityOwnersWithoutUnassigned, setQualityOwnersWithoutUnassigned] = useState([]);
    const [qualityOwners, setQualityOwners] = useState([]);
    const [selectedAssigneeRows, setSelectedAssigneeRows] = useState([]);
    const [assigneeCount, setAssigneeCount] = useState(0);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [preparingCSVForDownload, setPreparingCSVForDownload] = useState(false);
    const [csvReadyToDownload, setCSVReadyToDownload] = useState(false);

    const [isStatusEditorOpen, setIsStatusEditorOpen] = useState(false);
    const [statusRow, setStatusRow] = useState({});
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
    const [assignEditor, setAssignEditor] = useState(false);
    const [keys, setKeys] = useState({
        claimEventStatus: '0',
        claimEventType: '1',
        contract: '2',
        assigneeIds: '3'
    });
    const [isSearchFiltersDisabled, setIsSearchFiltersDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isAccessRestricted, setIsAccessRestricted] = useState(false);
    const [isOpenActionModal, setIsOpenActionModal] = useState(false);
    const [formType, setFormType] = useState('create');
    const [selectedRow, setSelectedRow] = useState({});
    const [openId, setOpenId] = useState('');
    const [errors, setErrors] = useState({});
    const [loadingMessage, setLoadingMessage] = useState(_NEW_SEARCH);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const claimEventStatusReasons = useSelector(
        (state) => state.entities.directusService.claimEventStatusReasons
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const claimQueueActionStatusDetails = useSelector(
        (state) => state.entities.directusService.claimQueueActionStatusDetails
    );
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const claimQueuesMetaData = useSelector(
        (state) => state.entities.participantService.claimQueuesMetaData
    );
    const loadingClaimQueuesSearch = useSelector(
        (state) => state.entities.participantService.loadingClaimQueuesSearch
    );
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        claimQueueActionStatusDetails?.length < 1 && dispatch(loadClaimQueueActionStatusDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
        setIsAccessRestricted(!hasRole([...acceptedRoles, ...limitedAccessRoles], roles));
    }, [roles]);

    useEffect(() => {
        const owners = users
            .filter((el) => el.userTypes?.find((entry) => entry.role === QUALITY))
            .map((el) => ({
                id: el.id,
                name: el.loginEmailAddress
            }));

        setQualityOwnersWithoutUnassigned(owners);
        const unassignedAndOwners = [...unassigned, ...owners];
        setQualityOwners(unassignedAndOwners);
    }, [users]);

    useEffect(() => {
        if (!claimQueueActionStatusDetails?.length) return;
        setCatId(
            claimQueueActionStatusDetails.find((el) => el.name === 'Corrective Action Taken')?.id
        );
    }, [claimQueueActionStatusDetails]);

    useEffect(() => {
        if (!Object.keys(filters)?.length && !searchTerm) setLoadingMessage(_NEW_SEARCH);
    }, [filters, searchTerm]);

    useEffect(() => {
        if (
            (Object.keys(filters).length || searchTerm) &&
            claimQueues.length > 0 &&
            !loadingClaimQueuesSearch &&
            'number' in claimQueuesMetaData &&
            !claimQueuesMetaData.last
        )
            dispatch(searchClaimQueue(filters, claimQueuesMetaData?.number + 1));
        else if (claimQueuesMetaData?.last && claimQueues.length > 0) {
            const updatedRows = claimQueues
                .map((el) => configRows(el))
                .map((el) => ({ ...el, checked: false }));
            setRows(updatedRows);
        } else {
            rows.length && setRows([]);
            if (Object.keys(filters).length || searchTerm) setLoadingMessage(CLAIMS_ERROR);
        }
    }, [claimQueues, claimQueuesMetaData, loadingClaimQueuesSearch]);

    useEffect(() => {
        if (csvReadyToDownload && claimQueues.length > 0) {
            startDownload();
        }
    }, [claimQueues, csvReadyToDownload]);

    useEffect(() => {
        if (successMessage === `Claim queue has been updated`)
            Object.keys(filters).length && dispatch(searchClaimQueue(filters, 0));
        else if (successMessage === `Claim queues have been updated`) closeToolbar();
    }, [successMessage]);

    // HELPER FUNCTIONS
    const configRows = (el) => ({
        ...el,
        statusName: getNameFromId(claimEventStatuses, el.statusId),
        actionStatus: el?.actionStatusId
            ? claimQueueActionStatusDetails?.find((entry) => entry.id === el.actionStatusId)?.name
            : 'zz'
    });

    const startDownload = () => {
        setCSVReadyToDownload(false);
        const newRows = claimQueues.map((el) => configRows(el));
        const name = `Live_Queue_Report_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`;
        const values = newRows.map((el) => [
            el.poNumber || '',
            el.ptCode,
            getNameFromId(contractDetails, filters.contractId),
            el.statusName,
            el.eventType,
            reverseFormatDate(el.eventDate),
            reverseFormatDate(el.lastStatusUpdated)
        ]);
        downloadCsv(csvHeaderFields, values, name);
        setPreparingCSVForDownload(false);
    };

    const loadingError = () => {
        if (claimEventStatuses?.length < 1) return CLAIM_EVENT_STATUSES_ERROR;
        if (claimEventTypes?.length < 1) return CLAIM_EVENT_TYPES_ERROR;
        if (contractDetails?.length < 1) return CONTRACT_DETAILS_ERROR;
    };

    const closeToolbar = () => {
        const deselectAssignees = selectedAssigneeRows.map((el) =>
            el.checked === true ? { ...el, checked: false } : el
        );
        setRows(deselectAssignees);
        setSelectedAssigneeRows(deselectAssignees);
        setAssigneeCount(0);
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const resetData = () => {
        rows.length && setRows([]);
        claimQueues?.length && dispatch(clearClaimQueues());
    };

    const cleanUp = () => {
        setKeys(clearKeys(keys));
        dispatch(clearClaimQueues());
    };

    // EVENT HANDLERS
    const onFetchMissingPagesForDownload = () => {
        setPreparingCSVForDownload(true);
        setCSVReadyToDownload(true);
    };

    const onCreateAction = (row) => {
        setSelectedRow(row);
        setIsOpenActionModal(true);
        setFormType('create');
    };

    const onEditAction = (row) => {
        setSelectedRow(row);
        setIsOpenActionModal(true);
        setFormType('edit');
    };

    const onActionModalCancel = () => {
        setIsOpenActionModal(false);
        setSelectedRow({});
    };

    const onUpdateActionStatus = () => {
        setIsOpenActionModal(false);
        setSelectedRow({});
    };

    const onToggleDropdown = (id) => setOpenId(id);

    const onStatusChangeEditor = (isOpen) => {
        setIsStatusEditorOpen(isOpen);
    };

    const onStatusRowChange = (row) => {
        setStatusRow(row);
        setIsStatusEditorOpen(true);
    };

    const onRowChange = (row) => {
        const selectedRows = rows.map((el) => (el.id === row.id ? row : el));
        setRows(selectedRows);
        setSelectedAssigneeRows(selectedRows);
        setAssigneeCount(selectedRows.filter((el) => el.checked === true).length);
    };

    const onAssignEditor = (setOpen) => setAssignEditor(setOpen);

    const onUpdateFilters = (key, value) => {
        if (value === filters[key]) return;
        resetData();
        if (!value || value.length === 0) setFilters(trimEntries([key], filters));
        else setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const onSearchDateRange = (searchRange) => {
        resetData();
        if (!searchRange) {
            if ('lastStatusUpdatedFrom' in filters && 'lastStatusUpdatedTo' in filters) {
                setFilters(trimEntries(['lastStatusUpdatedFrom', 'lastStatusUpdatedTo'], filters));
            }
        } else {
            const inDate = parse(searchRange.substring(0, 10), 'dd/MM/yyyy', new Date());
            const startDate = format(inDate, 'yyyy-MM-dd');
            const outDate = parse(searchRange.substring(13), 'dd/MM/yyyy', new Date());
            const endDate = format(outDate, 'yyyy-MM-dd');
            setFilters((prev) => ({
                ...prev,
                lastStatusUpdatedFrom: startDate,
                lastStatusUpdatedTo: endDate
            }));
        }
    };

    const onUpdateAssigneeIds = (chosenIds) => {
        chosenIds
            ? onUpdateFilters('assigneeIds', chosenIds)
            : onUpdateFilters('assigneeIds', null);
        setSelectedAssigneeIds(chosenIds);
    };

    const onUpdateStatusIds = (chosenId) => {
        chosenId ? onUpdateFilters('statusIds', [chosenId]) : onUpdateFilters('statusIds', null);
        setSelectedStatusId(chosenId);
    };

    const onSearchTermChange = (term) => {
        resetData();
        setIsSearchFiltersDisabled(!!term);
        setSearchTerm(term);
    };

    const onSearchParticipant = () => {
        if (searchTerm) dispatch(searchClaimQueue({ code: searchTerm }, 0));
    };

    const onSubmitFilterSearch = (e) => {
        e.preventDefault();
        const newErrors = validateFilters(filters);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(searchClaimQueue(filters, 0));
    };

    // RENDER
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <ValidationTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                isAccessRestricted={isAccessRestricted}
                toggleDropdown={onToggleDropdown}
                openId={openId}
                onStatusRowChange={onStatusRowChange}
                onCreateAction={onCreateAction}
                onEditAction={onEditAction}
                onRowChange={onRowChange}
            />
        ));

    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={app.pageContainer}>
            <div className={shared.headerBar}>
                <div className={app.sectionHeading}>Validation Queue</div>
                <div>
                    <label className={local.label} htmlFor="note">
                        Export to CSV
                    </label>
                    {preparingCSVForDownload ? (
                        <div className={local.loadingSpinnerWrapper}>
                            <div className={spinner.ddlLoader}>
                                <div className={spinner.loader} />
                            </div>
                        </div>
                    ) : (
                        <div className={local.downloadIconWrapper}>
                            <OnFileDownloadIcon
                                active={
                                    !isDisabled &&
                                    'contractId' in filters &&
                                    'lastStatusUpdatedFrom' in filters &&
                                    'lastStatusUpdatedTo' in filters &&
                                    claimQueues?.length > 0 &&
                                    claimQueuesMetaData?.last
                                }
                                onDownload={onFetchMissingPagesForDownload}
                            />
                        </div>
                    )}
                </div>
            </div>
            <form
                onSubmit={onSubmitFilterSearch}
                data-testid="form_start_validation_table_management">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'eventType'}
                            key={keys.claimEventType}
                            label={'Event Type'}
                            placeholder="Select event type..."
                            disabled={isAccessRestricted || isSearchFiltersDisabled}
                            menuItems={claimEventTypes || []}
                            selectedId={filters?.eventTypeId || ''}
                            selected={
                                claimEventTypes.find((el) => el.id === filters?.eventTypeId) || {}
                            }
                            onChange={(chosenId) => onUpdateFilters('eventTypeId', chosenId)}
                        />
                        <DateRangePicker
                            id="lastUpdated"
                            label="Last Updated"
                            disabled={isAccessRestricted || isSearchFiltersDisabled}
                            onSearch={onSearchDateRange}
                        />
                        <MultiSelect
                            id="assigneeIds"
                            key={keys.assigneeIds}
                            label="Claim Item Assignee"
                            placeholder="Select claim item assignee(s)..."
                            disabled={isDisabled || isSearchFiltersDisabled}
                            capitalizeList={false}
                            menuItems={qualityOwners || []}
                            preSelectedIds={selectedAssigneeIds || []}
                            preSelects={
                                qualityOwners.filter((el) => selectedAssigneeIds.includes(el.id)) ||
                                []
                            }
                            onChange={(chosenIds) => onUpdateAssigneeIds(chosenIds)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'contractId'}
                            key={keys.contract}
                            label={'Contract'}
                            placeholder="Select contract..."
                            disabled={isAccessRestricted || isSearchFiltersDisabled}
                            menuItems={contractDetails || []}
                            selectedId={filters?.contractId || ''}
                            selected={
                                contractDetails.find((el) => el.id === filters?.contractId) || {}
                            }
                            onChange={(chosenId) => onUpdateFilters('contractId', chosenId)}
                        />
                        <SingleSelect
                            id={'statusIds'}
                            key={keys.claimEventStatus}
                            label={'Status'}
                            placeholder="Select status..."
                            disabled={isAccessRestricted || isSearchFiltersDisabled}
                            menuItems={claimEventStatuses || []}
                            selectedId={selectedStatusId || ''}
                            selected={
                                claimEventStatuses.find((el) => el.id === selectedStatusId) || {}
                            }
                            onChange={(chosenId) => onUpdateStatusIds(chosenId)}
                        />

                        <RadioButtons
                            id="correctiveActivityTakenOnly"
                            label="Corrective Activity Taken Only"
                            disabled={isDisabled || isSearchFiltersDisabled}
                            value={!!filters.actionStatusId}
                            onChange={(option) =>
                                onUpdateFilters('actionStatusId', option ? catId : false)
                            }
                        />
                    </div>
                </div>
                <Button
                    id="validationClaimQueueSearch"
                    content="Search"
                    disabled={isAccessRestricted || isSearchFiltersDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>

            <div className={shared.claimsToolBarWrapper}>
                <div className={shared.claimsToolBar} data-testid="search_field">
                    <label className={form.formLabel}>Search PT ID & PO Number</label>
                    <SearchOnEnter
                        searchTerm={searchTerm}
                        placeholder="Search ID and PO..."
                        autocomplete="off"
                        disabled={isAccessRestricted}
                        search={onSearchParticipant}
                        onChange={onSearchTermChange}
                    />
                </div>
                {isStatusEditorOpen && (
                    <StatusEditor
                        open={isStatusEditorOpen}
                        onClose={() => onStatusChangeEditor(false)}
                        isDisabled={isAccessRestricted}
                        statusRow={statusRow}
                        claimEventStatuses={claimEventStatuses}
                        claimEventStatusReasons={claimEventStatusReasons}
                    />
                )}
            </div>

            {rows.length < 1 ? (
                loadingClaimQueuesSearch ? (
                    <LoadingSpinner />
                ) : (
                    <div>{loadingMessage}</div>
                )
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}

            {isOpenActionModal && (
                <ActionEditor
                    roles={roles}
                    formType={formType}
                    isOpen={isOpenActionModal}
                    selectedRow={selectedRow}
                    onCancel={onActionModalCancel}
                    onUpdateAction={onUpdateActionStatus}
                />
            )}
            <div hidden={!selectedAssigneeRows.some((el) => el.checked === true)}>
                <div className={local.assigneeToolBar}>
                    <div className={local.group}>
                        <span className={local.selected}>{assigneeCount}</span>
                        <span className={local.selectedText}>{'Selected'}</span>
                    </div>
                    <div className={local.group}>
                        <OnGroupIcon
                            onClick={() => onAssignEditor(true)}
                            title={'Select Assignee'}
                        />
                        <span className={local.assigneeText}>{'Assign Claim Item Assignee'}</span>
                    </div>
                    <div className={local.exitIcon}>
                        <OnExitIcon onClick={closeToolbar} />
                    </div>
                </div>
            </div>
            <AssigneeEditor
                open={assignEditor}
                onAssignEditor={onAssignEditor}
                qualityOwners={qualityOwnersWithoutUnassigned}
                rows={rows}
            />
        </div>
    );
};

export default ValidationTableManagement;
